import axios from 'axios'

const TOKEN_KEY = 'auth_token'
const EXPIRES_KEY = 'auth_expires_at'
const USER_KEY = 'auth_user'
const DEVICE_KEY = 'auth_device_id'

const toMs = (value) => {
  const text = String(value || '').trim()
  const t = Date.parse(text)
  if (Number.isFinite(t)) return t
  const m = text.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/)
  if (!m) return 0
  const [, y, mo, d, h = '0', mi = '0', s = '0'] = m
  const localTime = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s)
  ).getTime()
  return Number.isFinite(localTime) ? localTime : 0
}

export const getAuthToken = () => localStorage.getItem(TOKEN_KEY) || ''
export const getAuthExpiresAt = () => localStorage.getItem(EXPIRES_KEY) || ''
export const getAuthDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_KEY) || ''
  if (deviceId) return deviceId
  if (window.crypto?.randomUUID) {
    deviceId = window.crypto.randomUUID()
  } else {
    const bytes = new Uint8Array(16)
    window.crypto?.getRandomValues?.(bytes)
    deviceId = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('')
  }
  if (!deviceId) {
    deviceId = `${Date.now()}-${Math.random().toString(16).slice(2)}`
  }
  localStorage.setItem(DEVICE_KEY, deviceId)
  return deviceId
}

export const getAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || '{}') || {}
  } catch (_) {
    return {}
  }
}

export const isAuthValid = () => {
  const token = getAuthToken()
  const expiresAt = getAuthExpiresAt()
  if (!token || !expiresAt) return false
  return Date.now() < toMs(expiresAt)
}

export const saveAuth = ({ token, expires_at, user }) => {
  localStorage.setItem(TOKEN_KEY, String(token || ''))
  localStorage.setItem(EXPIRES_KEY, String(expires_at || ''))
  localStorage.setItem(USER_KEY, JSON.stringify(user || {}))

  // 兼容项目里已存在的角色/门店读取逻辑
  if (user?.role) localStorage.setItem('role', String(user.role))
  if (user?.store) localStorage.setItem('store', String(user.store))
  if (user?.name) localStorage.setItem('erpo_user_name', String(user.name))

  window.dispatchEvent(new Event('auth-changed'))
}

export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(EXPIRES_KEY)
  localStorage.removeItem(USER_KEY)
  localStorage.removeItem('role')
  localStorage.removeItem('store')
  localStorage.removeItem('erpo_user_name')
  window.dispatchEvent(new Event('auth-changed'))
}

const pageViewCache = new Map()
export const reportPageView = async ({ path, title }) => {
  const pagePath = String(path || '').trim()
  if (!pagePath || pagePath === '/login' || pagePath === '/launcher-migrate' || window.location.pathname === '/launcher-migrate' || !isAuthValid()) return
  const key = `${getAuthUser()?.id || ''}:${pagePath}`
  const now = Date.now()
  if (now - Number(pageViewCache.get(key) || 0) < 5 * 60 * 1000) return
  pageViewCache.set(key, now)
  try {
    await fetch('/api/audit/page-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: pagePath, title: String(title || '') }),
    })
  } catch (_) {
    // 页面访问日志不能影响正常使用。
  }
}

const isApiUrl = (url) => /(^|\/)api(\/|$)/.test(String(url || ''))
const isMigrationApi = (url) => /^\/api\/browser-migration\/(config|prepare|consume)$/.test(String(url || ''))
const isLoginApi = (url) => /(^|\/)api\/auth\/login($|\?)/.test(String(url || ''))
const isVipAuthApi = (url) => /(^|\/)api\/vip-auth\//.test(String(url || ''))
const redirectToLogin = () => {
  // A revoked old-origin session must not interrupt the device/settings handoff.
  // The migration endpoint validates the session and returns auth=null if needed.
  if (window.location.pathname === '/launcher-migrate') return
  clearAuth()
  if (!window.location.pathname.startsWith('/login')) {
    const from = `${window.location.pathname}${window.location.search || ''}`
    window.location.assign(`/login?redirect=${encodeURIComponent(from)}`)
  }
}

let fetchWrapped = false
export const setupAuthFetch = () => {
  if (fetchWrapped) return
  fetchWrapped = true
  const rawFetch = window.fetch.bind(window)
  window.fetch = async (input, init = {}) => {
    const req = { ...(init || {}) }
    const url = typeof input === 'string' ? input : (input?.url || '')
    // Migration must read the old ID before anything creates one on the new origin.
    // Its handlers perform their own origin and one-time handoff validation.
    if (isMigrationApi(url)) return rawFetch(input, req)
    const headers = new Headers(req.headers || {})
    const token = getAuthToken()
    if (isApiUrl(url) && !isVipAuthApi(url)) {
      headers.set('X-Device-Id', getAuthDeviceId())
      if (token) headers.set('Authorization', `Bearer ${token}`)
    }
    req.headers = headers
    const res = await rawFetch(input, req)
    if (isApiUrl(url) && !isVipAuthApi(url) && res.status === 401 && !isLoginApi(url)) {
      redirectToLogin()
    }
    return res
  }
}

let axiosWrapped = false
export const setupAuthAxios = () => {
  if (axiosWrapped) return
  axiosWrapped = true

  axios.interceptors.request.use((config) => {
    const cfg = config || {}
    const url = String(cfg.url || '')
    const token = getAuthToken()
    if (isApiUrl(url) && !isVipAuthApi(url)) {
      if (!cfg.headers) cfg.headers = {}
      if (typeof cfg.headers.set === 'function') {
        cfg.headers.set('X-Device-Id', getAuthDeviceId())
        if (token) cfg.headers.set('Authorization', `Bearer ${token}`)
      } else {
        cfg.headers['X-Device-Id'] = getAuthDeviceId()
        if (token) cfg.headers.Authorization = `Bearer ${token}`
      }
    }
    return cfg
  })

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const url = String(error?.config?.url || '')
      const status = error?.response?.status
      if (isApiUrl(url) && !isVipAuthApi(url) && status === 401 && !isLoginApi(url)) {
        redirectToLogin()
      }
      return Promise.reject(error)
    }
  )
}
